import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { ArrowRight, Zap } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { SHOWCASE } from "@/lib/showcase";

export default function Showcase() {
  usePageMeta({
    title: "Showcase — Real Blueprints from Real Sites | MonetizeGPS",
    description: "Five real monetization Blueprints — from the NFL to an indie app. See exactly what MonetizeGPS delivers before you buy.",
    path: "/showcase",
  });
  return (
    <div className="min-h-screen grain" data-testid="showcase-page">
      <Header />

      {/* HERO */}
      <section className="border-b-2 border-[#111]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-14">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-3">
            § Showcase · Real Blueprints, real sites
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9]">
            Big or small.<br />Any country.<br />
            <span className="text-[#2437FF]">MonetizeGPS gets it done.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-[#333]">
            Five real Blueprints, generated by the same engine you can use right now — from the NFL
            to an indie app. Every play scored, ranked, and backed by evidence from the site itself.
          </p>
        </div>
      </section>

      {/* CARDS */}
      <section className="border-b-2 border-[#111] bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SHOWCASE.map((s) => (
              <Link
                key={s.id}
                to={`/report/${s.id}`}
                className="group border-2 border-[#111] bg-[#F4F4F0] p-6 shadow-brutal hover:shadow-brutal-lg hover:-translate-y-0.5 transition-all"
                data-testid={`showcase-card-${s.domain.replace(/\./g, "-")}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#555] border border-[#111] px-2 py-0.5">
                    {s.tag}
                  </span>
                  <span className="text-2xl" aria-hidden="true">{s.flag}</span>
                </div>
                <div className="mt-4 font-serif-display text-3xl font-black tracking-tight leading-none">
                  {s.name}
                </div>
                <div className="mt-1 font-mono-plex text-[11px] text-[#555]">{s.domain}</div>
                <div className="mt-5 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 bg-[#111] text-[#F4F4F0] font-mono-plex text-[11px] uppercase tracking-widest px-2.5 py-1.5">
                    <Zap size={11} className="text-[#FF331F]" /> {s.topPlay}
                  </span>
                  <span className="font-serif-display text-2xl font-black text-[#FF331F]">{s.score}</span>
                  <span className="font-mono-plex text-[9px] uppercase tracking-[0.2em] text-[#555]">fit score</span>
                </div>
                <p className="mt-4 text-sm text-[#333] leading-relaxed">{s.teaser}</p>
                <div className="mt-5 inline-flex items-center gap-2 font-mono-plex text-xs uppercase tracking-[0.25em] text-[#FF331F] group-hover:gap-3 transition-all">
                  See the full Blueprint <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b-2 border-[#111]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-serif-display text-2xl md:text-3xl font-black tracking-tight leading-none">
              Your site is next.
            </div>
            <div className="mt-2 font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#555]">
              1 free preview · No signup needed · Ready in under a minute
            </div>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#FF331F] text-white px-6 py-4 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#111] transition-colors shadow-brutal"
            data-testid="showcase-analyze-cta"
          >
            Analyze my URL <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* DISCLAIMER */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-8">
        <p className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-[#888] leading-relaxed">
          * Independent analysis based on publicly available data. Not affiliated with or endorsed by any brand shown.
        </p>
      </div>
    </div>
  );
}
